import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Textarea } from "../../../foundation/altzero_base/base/components/ui/textarea";
import { Separator } from "../../../foundation/altzero_base/base/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../foundation/altzero_base/base/components/ui/popover";
import {
  FileText,
  Download,
  Upload,
  Image,
  Table,
  Type,
  Palette,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Plus,
  Minus,
  Eye,
  Edit,
  Save,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Printer,
  Share,
  Settings,
  Sparkles,
  Zap,
  Target,
  Award,
  TrendingUp,
  BarChart3,
  PieChart,
  Calendar,
  MapPin,
  Building,
  Users,
  DollarSign,
  Leaf,
  Globe
} from "lucide-react";

import { useDocumentForm } from "../contexts/DocumentFormContext";
import { ENERGY_RETAILERS } from "../types/offerTypes";
import { loadTemplateContent, DEFAULT_TEMPLATES } from "../services/documentLibraryService";
import { getTenderCosts } from "../services/calculationApiService";
import type { ContractCostBreakdown } from "../types/calculation";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { parseMarkdownSections } from "../utils/sectionUtils";
import { formatTermLabel } from '../lib/termLabel';

// Generate proposal content using actual template
const generateProposalFromTemplate = (
  templateContent: string,
  clientData: any,
  offers: any[],
  recommendedOffer: any,
  costVariables: Record<string, string> = {}
) => {
  // Safe defaults for all data
  const safeClientData = clientData || { name: 'Client Name', industry: 'Commercial', contactPerson: 'Contact Person' };
  const safeOffers = offers || [];
  const safeRecommendedOffer = recommendedOffer || {
    retailerId: 'energyaustralia',
    retailerName: 'EnergyAustralia',
    greenPercent: 20,
    estimatedAnnualCost: 50000
  };

  const retailer = ENERGY_RETAILERS.find(r => r.id === safeRecommendedOffer?.retailerId) || ENERGY_RETAILERS[0];
  const currentDate = new Date().toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Replace template variables with actual data
  let processedContent = templateContent || '';

  // Build replacement map — cost variables first (lowest priority), then specific fields override
  const replacements: Record<string, string> = {
    // Live cost variables populated from the same calculation API as the compare page
    ...costVariables,
    // Core proposal fields (override any cost variable key collisions)
    '{{client_name}}': safeClientData.name || 'Client Name',
    '{{client_industry}}': safeClientData.industry || 'Commercial',
    '{{contact_person}}': safeClientData.contactPerson || 'Contact Person',
    '{{date}}': currentDate,
    '{{retailer_name}}': retailer.name,
    '{{estimated_cost}}': new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(safeRecommendedOffer.estimatedAnnualCost || 50000),
    '{{green_percent}}': `${safeRecommendedOffer.greenPercent || 20}%`,
    '{{term_months}}': formatTermLabel(safeRecommendedOffer.termMonths || 48),
  };

  // Apply replacements
  Object.entries(replacements).forEach(([placeholder, value]) => {
    processedContent = processedContent.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  });

  // Parse markdown content into sections using the centralized function
  const parsedSections = parseMarkdownSections(processedContent);

  // Convert parsed sections to the format expected by the proposal editor
  const sections = parsedSections.map(section => ({
    id: section.id,
    title: section.title,
    content: section.content
  }));

  // If no sections were found, create a single section with all content
  if (sections.length === 0) {
    // If template content is empty, show a message about the template
    const defaultContent = processedContent.trim() || `
      <h2>Template Content</h2>
      <p>This proposal is based on the selected template.</p>
      <p><strong>Client:</strong> ${safeClientData.name}</p>
      <p><strong>Date:</strong> ${currentDate}</p>
      <p><em>Template content will appear here once loaded.</em></p>
    `;

    sections.push({
      id: 'content',
      title: 'Proposal Content',
      content: defaultContent
    });
  }

  return {
    title: `Energy Proposal for ${safeClientData.name}`,
    subtitle: `Prepared by Choice Energy`,
    date: currentDate,
    sections: sections.map(section => ({
      ...section,
      content: section.content // Keep raw markdown content for ReactMarkdown rendering
    }))
  };
};




const ProposalEditor: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template') || '';

  const { clientData, offers, selectedTemplate, templates, tenderId } = useDocumentForm();

  // Ensure offers is always an array and provide safe defaults
  const safeOffers = offers || [];
  const safeClientData = clientData || { name: 'Client Name', sites: [] };
  // Get the first offer with recommended months, or fallback to first offer
  const recommendedOffer = safeOffers.find(offer => offer.recommendedMonths?.length > 0) || safeOffers[0] || {
    retailerId: 'energyaustralia',
    retailerName: 'EnergyAustralia',
    greenPercent: 20,
    estimatedAnnualCost: 50000,
    termMonths: 48
  };

  // Clean template content by removing metadata comments
  const cleanTemplateContent = (content: string): string => {
    // Remove HTML comments with metadata
    return content
      .replace(/<!--\s*METADATA:.*?-->/g, '')
      .replace(/<!--.*?-->/g, '')
      .trim();
  };

  // Get template content - OPTIMIZED for performance
  const getTemplateContent = async (): Promise<string> => {
    const currentTemplateId = templateId || selectedTemplate;

    // If we have a template ID, try to load it from Supabase first
    if (currentTemplateId) {
      try {
        const content = await loadTemplateContent(currentTemplateId);
        if (content && content.trim()) {
          return cleanTemplateContent(content);
        }
      } catch (error) {
        // If it's a default template ID, try to get it from DEFAULT_TEMPLATES
        if (currentTemplateId.startsWith('default-')) {
          const defaultTemplate = DEFAULT_TEMPLATES.find(t => t.id === currentTemplateId);
          if (defaultTemplate?.content) {
            return cleanTemplateContent(defaultTemplate.content);
          }
        }
      }
    }

    // Last resort: use first default template
    const defaultTemplate = DEFAULT_TEMPLATES[0];
    return cleanTemplateContent(defaultTemplate?.content || '');
  };

  const [proposalData, setProposalData] = useState(() => {
    // Initialize with empty data, will be loaded async
    return {
      title: 'Loading Proposal...',
      subtitle: 'Prepared by Choice Energy',
      date: new Date().toLocaleDateString('en-AU'),
      sections: []
    };
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true);
  // Cost variables built from the same calculation API as the Compare page
  const [costVariables, setCostVariables] = useState<Record<string, string>>({});

  // Retailer name → template slug mapping (mirrors templateVariables.ts)
  const RETAILER_SLUG_MAP: Record<string, string> = {
    'AGL Energy': 'agl', 'Origin Energy': 'origin', 'EnergyAustralia': 'energyaustralia',
    'Energy Australia': 'energyaustralia', 'Red Energy': 'redenergy', 'Alinta Energy': 'alinta',
    'Simply Energy': 'simply', 'Momentum Energy': 'momentum', 'Powershop': 'powershop',
    'Lumo Energy': 'lumo', 'GloBird Energy': 'globird',
  };
  const getRetailerSlug = (name: string) => {
    if (RETAILER_SLUG_MAP[name]) return RETAILER_SLUG_MAP[name];
    const lower = name.toLowerCase();
    const found = Object.entries(RETAILER_SLUG_MAP).find(([k]) => k.toLowerCase() === lower);
    return found ? found[1] : name.toLowerCase().replace(/[^a-z0-9]/g, '');
  };

  // Fetch cost breakdowns (same source as Compare page) and build template variable map
  useEffect(() => {
    if (!tenderId) return;
    const fetchCosts = async () => {
      try {
        const result = await getTenderCosts(tenderId);
        if (!result.success || !result.data) return;
        const fmt = (n: number) => (n ?? 0).toFixed(2);
        const vars: Record<string, string> = {};
        const bestPerTerm: Record<string, { perYear: number; bd: ContractCostBreakdown }> = {};
        result.data.offers.forEach(offer => {
          const slug = getRetailerSlug(offer.retailer_name);
          Object.entries(offer.costs_by_term).forEach(([termStr, bd]) => {
            const term = parseInt(termStr);
            if (isNaN(term)) return;
            vars[`{{${slug}_${term}_total}}`]   = fmt(bd.total);
            vars[`{{${slug}_${term}_energy}}`]  = fmt(bd.energy);
            vars[`{{${slug}_${term}_environ}}`] = fmt(bd.environment);
            vars[`{{${slug}_${term}_network}}`] = fmt(bd.network);
            vars[`{{${slug}_${term}_others}}`]  = fmt(bd.others);
            vars[`{{${slug}_${term}_peryear}}`] = fmt(bd.perYear);
            const prev = bestPerTerm[termStr];
            if (!prev || (bd.perYear ?? 0) < prev.perYear) {
              bestPerTerm[termStr] = { perYear: bd.perYear ?? 0, bd };
            }
          });
        });
        // Generic Xm_ variables — cheapest offer per term
        Object.entries(bestPerTerm).forEach(([termStr, { bd }]) => {
          const term = parseInt(termStr);
          if (isNaN(term)) return;
          vars[`{{${term}m_total}}`]   = fmt(bd.total);
          vars[`{{${term}m_energy}}`]  = fmt(bd.energy);
          vars[`{{${term}m_environ}}`] = fmt(bd.environment);
          vars[`{{${term}m_network}}`] = fmt(bd.network);
          vars[`{{${term}m_others}}`]  = fmt(bd.others);
          vars[`{{${term}m_peryear}}`] = fmt(bd.perYear);
        });
        setCostVariables(vars);
      } catch (err) {
        console.error('[ProposalEditor] Failed to load cost variables:', err);
      }
    };
    fetchCosts();
  }, [tenderId]);

  const editorRef = useRef<HTMLDivElement>(null);
  const totalPages = proposalData.sections.length + 1; // +1 for title page

  // Load template content and update proposal data - OPTIMIZED to prevent flickering
  useEffect(() => {
    const loadTemplateAndGenerateProposal = async () => {
      const currentTemplateId = templateId || selectedTemplate;
      if (!currentTemplateId) return;

      setIsLoadingTemplate(true);
      try {
        const templateContent = await getTemplateContent();
        const updatedProposalData = generateProposalFromTemplate(templateContent, safeClientData, safeOffers, recommendedOffer, costVariables);
        setProposalData(updatedProposalData);

        // Set first section as selected if none selected
        if (!selectedSection && updatedProposalData.sections.length > 0) {
          setSelectedSection(updatedProposalData.sections[0].id);
        }
      } catch (error) {
        console.error('Error loading template:', error);
        // Fallback to default content
        const defaultTemplate = DEFAULT_TEMPLATES[0];
        const fallbackContent = defaultTemplate?.content || '';
        const fallbackProposalData = generateProposalFromTemplate(fallbackContent, safeClientData, safeOffers, recommendedOffer, costVariables);
        setProposalData(fallbackProposalData);
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    loadTemplateAndGenerateProposal();
  }, [templateId, selectedTemplate, costVariables]); // Re-render when live cost variables load

  // Ensure a section is selected when proposal data changes - OPTIMIZED
  useEffect(() => {
    if (!selectedSection && proposalData.sections.length > 0 && !isLoadingTemplate) {
      setSelectedSection(proposalData.sections[0].id);
    }
  }, [proposalData.sections.length, selectedSection, isLoadingTemplate]); // Optimized dependencies

  const handleSectionEdit = (sectionId: string, newContent: string) => {
    setProposalData(prev => ({
      ...prev,
      sections: prev.sections.map(section => 
        section.id === sectionId 
          ? { ...section, content: newContent }
          : section
      )
    }));
  };

  const handleExport = async (format: string) => {
    try {
      if (format === 'pdf') {
        // Create a new window with the proposal content for PDF export
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>${proposalData.title}</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
                h2 { color: #1e40af; margin-top: 30px; }
                h3 { color: #3730a3; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                th { background-color: #f0f8ff; font-weight: bold; }
                .title-page { text-align: center; page-break-after: always; padding: 100px 0; }
                .section { page-break-before: always; }
                @media print {
                  body { margin: 0; }
                  .no-print { display: none; }
                }
              </style>
            </head>
            <body>
              <div class="title-page">
                <h1>${proposalData.title}</h1>
                <h2>${proposalData.subtitle}</h2>
                <div style="margin: 50px 0;">
                  <div style="width: 100px; height: 100px; background: linear-gradient(135deg, #2563eb, #7c3aed); border-radius: 50%; margin: 0 auto; display: flex; align-items: center; justify-content: center; color: white; font-size: 40px;">⚡</div>
                </div>
                <p><strong>Prepared for:</strong> ${safeClientData.name}</p>
                <p><strong>Date:</strong> ${proposalData.date}</p>
                <p><strong>Prepared by:</strong> Choice Energy</p>
              </div>
              ${proposalData.sections.map(section => `
                <div class="section">
                  ${section.content}
                </div>
              `).join('')}
            </body>
            </html>
          `;

          printWindow.document.write(htmlContent);
          printWindow.document.close();

          // Wait for content to load then print
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 1000);
        }
      } else if (format === 'docx') {
        // For Word export, we'll create a downloadable HTML file that can be opened in Word
        const htmlContent = `
          <!DOCTYPE html>
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
          <head>
            <meta charset="utf-8">
            <title>${proposalData.title}</title>
            <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>90</w:Zoom><w:DoNotPromptForConvert/><w:DoNotShowInsertAsIcon/></w:WordDocument></xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
              h2 { color: #1e40af; margin-top: 30px; }
              h3 { color: #3730a3; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              th { background-color: #f0f8ff; font-weight: bold; }
            </style>
          </head>
          <body>
            <div style="text-align: center; margin-bottom: 50px;">
              <h1>${proposalData.title}</h1>
              <h2>${proposalData.subtitle}</h2>
              <p><strong>Prepared for:</strong> ${safeClientData.name}</p>
              <p><strong>Date:</strong> ${proposalData.date}</p>
              <p><strong>Prepared by:</strong> Choice Energy</p>
            </div>
            ${proposalData.sections.map(section => section.content).join('<div style="page-break-before: always;"></div>')}
          </body>
          </html>
        `;

        const blob = new Blob([htmlContent], { type: 'application/msword' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${proposalData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.doc`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (format === 'html') {
        // Export as HTML file
        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="utf-8">
            <title>${proposalData.title}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
              h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
              h2 { color: #1e40af; margin-top: 30px; }
              h3 { color: #3730a3; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
              th { background-color: #f0f8ff; font-weight: bold; }
              .title-page { text-align: center; margin-bottom: 50px; padding: 50px 0; border-bottom: 2px solid #eee; }
              .section { margin: 40px 0; }
              img { max-width: 100%; height: auto; }
            </style>
          </head>
          <body>
            <div class="title-page">
              <h1>${proposalData.title}</h1>
              <h2>${proposalData.subtitle}</h2>
              <div style="margin: 30px 0;">
                <div style="width: 80px; height: 80px; background: linear-gradient(135deg, #2563eb, #7c3aed); border-radius: 50%; margin: 0 auto; display: flex; align-items: center; justify-content: center; color: white; font-size: 30px;">⚡</div>
              </div>
              <p><strong>Prepared for:</strong> ${safeClientData.name}</p>
              <p><strong>Date:</strong> ${proposalData.date}</p>
              <p><strong>Prepared by:</strong> Choice Energy</p>
            </div>
            ${proposalData.sections.map(section => `
              <div class="section">
                ${section.content}
              </div>
            `).join('')}
          </body>
          </html>
        `;

        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${proposalData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      setShowExportDialog(false);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Memoize currentSection to prevent re-renders and flickering
  const currentSection = useMemo(() => {
    return proposalData.sections.find(s => s.id === selectedSection);
  }, [proposalData.sections, selectedSection]);

  // Rich text editing functions
  const formatText = (command: string, value?: string) => {
    document.execCommand(command, false, value);
  };

  const insertTable = () => {
    const tableHTML = `
      <div class="table-container" style="margin: 20px 0; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
        <div class="table-toolbar" style="background: #f8f9fa; padding: 8px; border-bottom: 1px solid #ddd; display: flex; gap: 8px; align-items: center; flex-wrap: wrap;">
          <button onclick="addTableRow(this)" style="background: #007bff; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 12px; transition: all 0.2s;">+ Add Row</button>
          <button onclick="addTableColumn(this)" style="background: #28a745; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 12px; transition: all 0.2s;">+ Add Column</button>
          <button onclick="removeTableRow(this)" style="background: #dc3545; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 12px; transition: all 0.2s;">- Remove Row</button>
          <button onclick="removeTableColumn(this)" style="background: #fd7e14; color: white; border: none; padding: 4px 8px; border-radius: 4px; cursor: pointer; font-size: 12px; transition: all 0.2s;">- Remove Column</button>
          <span style="color: #6c757d; font-size: 11px; margin-left: auto;">Tip: Click cells to edit, use Tab to navigate</span>
        </div>
        <table border="1" style="width: 100%; border-collapse: collapse;" class="editable-table">
          <thead>
            <tr style="background-color: #f0f8ff;">
              <th style="padding: 12px; text-align: left;" contenteditable="true">Header 1</th>
              <th style="padding: 12px; text-align: left;" contenteditable="true">Header 2</th>
              <th style="padding: 12px; text-align: left;" contenteditable="true">Header 3</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 10px;" contenteditable="true">Cell 1</td>
              <td style="padding: 10px;" contenteditable="true">Cell 2</td>
              <td style="padding: 10px;" contenteditable="true">Cell 3</td>
            </tr>
            <tr>
              <td style="padding: 10px;" contenteditable="true">Cell 4</td>
              <td style="padding: 10px;" contenteditable="true">Cell 5</td>
              <td style="padding: 10px;" contenteditable="true">Cell 6</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    if (currentSection) {
      handleSectionEdit(selectedSection, currentSection.content + tableHTML);
    }
  };

  const insertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageHTML = `<img src="${e.target?.result}" style="max-width: 100%; height: auto; margin: 20px 0;" alt="Inserted image" />`;
          if (currentSection) {
            handleSectionEdit(selectedSection, currentSection.content + imageHTML);
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  // Table manipulation functions
  useEffect(() => {
    // Add keyboard navigation for tables
    const handleTableKeydown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.contentEditable === 'true' && target.closest('.editable-table')) {
        if (e.key === 'Tab') {
          e.preventDefault();
          const table = target.closest('table');
          const cells = Array.from(table?.querySelectorAll('td[contenteditable="true"], th[contenteditable="true"]') || []);
          const currentIndex = cells.indexOf(target);

          if (e.shiftKey) {
            // Move to previous cell
            const prevCell = cells[currentIndex - 1] as HTMLElement;
            if (prevCell) prevCell.focus();
          } else {
            // Move to next cell
            const nextCell = cells[currentIndex + 1] as HTMLElement;
            if (nextCell) nextCell.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleTableKeydown);

    // Add global table manipulation functions
    (window as any).addTableRow = (button: HTMLElement) => {
      const table = button.closest('.table-container')?.querySelector('table tbody');
      if (table) {
        const firstRow = table.querySelector('tr');
        if (firstRow) {
          const newRow = firstRow.cloneNode(true) as HTMLElement;
          const cells = newRow.querySelectorAll('td');
          const rowCount = table.querySelectorAll('tr').length + 1;
          cells.forEach((cell, index) => {
            cell.textContent = `Cell ${rowCount}-${index + 1}`;
            cell.setAttribute('contenteditable', 'true');
          });
          table.appendChild(newRow);

          // Focus on the first cell of the new row
          const firstCell = newRow.querySelector('td');
          if (firstCell) {
            firstCell.focus();
          }
        }
      }
    };

    (window as any).addTableColumn = (button: HTMLElement) => {
      const tableContainer = button.closest('.table-container');
      const table = tableContainer?.querySelector('table');
      if (table) {
        const currentColumnCount = table.querySelector('thead tr')?.children.length || 0;

        // Add header cell
        const headerRow = table.querySelector('thead tr');
        if (headerRow) {
          const newHeader = document.createElement('th');
          newHeader.style.padding = '12px';
          newHeader.style.textAlign = 'left';
          newHeader.style.backgroundColor = '#f0f8ff';
          newHeader.contentEditable = 'true';
          newHeader.textContent = `Header ${currentColumnCount + 1}`;
          headerRow.appendChild(newHeader);
        }

        // Add cells to all body rows
        const bodyRows = table.querySelectorAll('tbody tr');
        bodyRows.forEach((row, rowIndex) => {
          const newCell = document.createElement('td');
          newCell.style.padding = '10px';
          newCell.contentEditable = 'true';
          newCell.textContent = `Cell ${rowIndex + 1}-${currentColumnCount + 1}`;
          row.appendChild(newCell);
        });

        // Focus on the new header cell
        const newHeaderCell = headerRow?.lastElementChild as HTMLElement;
        if (newHeaderCell) {
          newHeaderCell.focus();
        }
      }
    };

    (window as any).removeTableRow = (button: HTMLElement) => {
      const table = button.closest('.table-container')?.querySelector('table tbody');
      if (table) {
        const rows = table.querySelectorAll('tr');
        if (rows.length > 1) {
          rows[rows.length - 1].remove();
        } else {
          alert('Cannot remove the last row. Table must have at least one row.');
        }
      }
    };

    (window as any).removeTableColumn = (button: HTMLElement) => {
      const tableContainer = button.closest('.table-container');
      const table = tableContainer?.querySelector('table');
      if (table) {
        const headerRow = table.querySelector('thead tr');
        const columnCount = headerRow?.children.length || 0;

        if (columnCount > 1) {
          // Remove last header cell
          headerRow?.lastElementChild?.remove();

          // Remove last cell from each body row
          const bodyRows = table.querySelectorAll('tbody tr');
          bodyRows.forEach(row => {
            row.lastElementChild?.remove();
          });
        } else {
          alert('Cannot remove the last column. Table must have at least one column.');
        }
      }
    };

    return () => {
      // Cleanup keyboard event listener
      document.removeEventListener('keydown', handleTableKeydown);

      // Cleanup global functions
      delete (window as any).addTableRow;
      delete (window as any).addTableColumn;
      delete (window as any).removeTableRow;
      delete (window as any).removeTableColumn;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Add CSS for table editing */}
      <style>{`
        .table-container {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .table-container .editable-table td[contenteditable="true"]:focus,
        .table-container .editable-table th[contenteditable="true"]:focus {
          outline: 2px solid #007bff;
          outline-offset: -2px;
          background-color: #f8f9ff;
          box-shadow: inset 0 0 0 1px #007bff;
        }

        .table-container .table-toolbar button:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .table-container .table-toolbar button:active {
          transform: translateY(0);
        }

        .table-container .editable-table {
          border: none;
          font-family: inherit;
        }

        .table-container .editable-table td,
        .table-container .editable-table th {
          border: 1px solid #dee2e6;
          transition: all 0.2s ease;
          position: relative;
        }

        .table-container .editable-table td:hover,
        .table-container .editable-table th:hover {
          background-color: #f8f9fa;
          border-color: #adb5bd;
        }

        .table-container .editable-table th {
          font-weight: 600;
          color: #495057;
        }

        .table-container .editable-table td {
          color: #212529;
        }

        .table-container .editable-table td:empty:before,
        .table-container .editable-table th:empty:before {
          content: 'Click to edit';
          color: #6c757d;
          font-style: italic;
        }
      `}</style>
      {/* Header Toolbar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Navigation */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/tender/documents/new')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Overview
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-lg font-semibold text-gray-900">Proposal Editor</h1>
              <Badge variant="secondary">Template-Based</Badge>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditing(!isEditing)}>
                {isEditing ? <Eye className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
                {isEditing ? 'Preview' : 'Edit'}
              </Button>
              
              <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Export Proposal</DialogTitle>
                    <DialogDescription>
                      Choose your preferred export format
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <Button onClick={() => handleExport('pdf')} className="h-auto p-4 flex flex-col gap-2">
                      <FileText className="w-8 h-8" />
                      <span>PDF Document</span>
                    </Button>
                    <Button onClick={() => handleExport('docx')} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                      <FileText className="w-8 h-8" />
                      <span>Word Document</span>
                    </Button>
                    <Button onClick={() => handleExport('html')} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                      <Globe className="w-8 h-8" />
                      <span>HTML File</span>
                    </Button>
                    <Button onClick={() => window.print()} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                      <Printer className="w-8 h-8" />
                      <span>Print</span>
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Left Sidebar - Section Navigation */}
          <div className="col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Document Sections</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {/* Title Page */}
                  <button
                    onClick={() => setSelectedSection('title')}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-l-4 ${
                      selectedSection === 'title' ? 'border-blue-500 bg-blue-50' : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Title Page
                    </div>
                  </button>
                  
                  {/* Content Sections */}
                  {proposalData.sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => setSelectedSection(section.id)}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-l-4 ${
                        selectedSection === section.id ? 'border-blue-500 bg-blue-50' : 'border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-gray-200 text-xs flex items-center justify-center">
                          {index + 1}
                        </span>
                        {section.title}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Page Navigation */}
            <Card className="mt-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="col-span-9">
            {/* Rich Text Editing Toolbar */}
            {isEditing && selectedSection !== 'title' && (
              <Card className="mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 border-r pr-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('bold')}
                        className="p-2"
                      >
                        <Bold className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('italic')}
                        className="p-2"
                      >
                        <Italic className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('underline')}
                        className="p-2"
                      >
                        <Underline className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-1 border-r pr-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('justifyLeft')}
                        className="p-2"
                      >
                        <AlignLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('justifyCenter')}
                        className="p-2"
                      >
                        <AlignCenter className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('justifyRight')}
                        className="p-2"
                      >
                        <AlignRight className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-1 border-r pr-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('insertUnorderedList')}
                        className="p-2"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => formatText('insertOrderedList')}
                        className="p-2"
                      >
                        <ListOrdered className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex items-center gap-1 border-r pr-2">
                      <Select onValueChange={(value) => formatText('fontSize', value)}>
                        <SelectTrigger className="w-20 h-8">
                          <SelectValue placeholder="Size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Small</SelectItem>
                          <SelectItem value="3">Normal</SelectItem>
                          <SelectItem value="5">Large</SelectItem>
                          <SelectItem value="7">XLarge</SelectItem>
                        </SelectContent>
                      </Select>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="p-2">
                            <Palette className="w-4 h-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48">
                          <div className="grid grid-cols-6 gap-2">
                            {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#008000', '#FFC0CB', '#A52A2A'].map(color => (
                              <button
                                key={color}
                                className="w-6 h-6 rounded border"
                                style={{ backgroundColor: color }}
                                onClick={() => formatText('foreColor', color)}
                              />
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={insertTable}
                        className="p-2"
                      >
                        <Table className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={insertImage}
                        className="p-2"
                      >
                        <Image className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="min-h-[800px]">
              <CardContent className="p-8">
                {/* Title Page */}
                {selectedSection === 'title' && (
                  <div className="text-center space-y-8 py-16">
                    <div className="space-y-4">
                      <h1 className="text-4xl font-bold text-gray-900">
                        {isEditing ? (
                          <Input
                            value={proposalData.title}
                            onChange={(e) => setProposalData(prev => ({ ...prev, title: e.target.value }))}
                            className="text-center text-4xl font-bold border-none shadow-none"
                          />
                        ) : (
                          proposalData.title
                        )}
                      </h1>
                      <h2 className="text-xl text-gray-600">
                        {isEditing ? (
                          <Input
                            value={proposalData.subtitle}
                            onChange={(e) => setProposalData(prev => ({ ...prev, subtitle: e.target.value }))}
                            className="text-center text-xl border-none shadow-none"
                          />
                        ) : (
                          proposalData.subtitle
                        )}
                      </h2>
                    </div>
                    
                    <div className="py-8">
                      <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto flex items-center justify-center">
                        <Zap className="w-16 h-16 text-white" />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-lg text-gray-700">Prepared for: <strong>{safeClientData.name}</strong></p>
                      <p className="text-gray-600">Date: {proposalData.date}</p>
                      <p className="text-gray-600">Prepared by: Choice Energy</p>
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {isLoadingTemplate && (
                  <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading template content...</p>
                    </div>
                  </div>
                )}

                {/* Content Sections */}
                {!isLoadingTemplate && currentSection && selectedSection !== 'title' && (
                  <div className="prose max-w-none">
                    {isEditing ? (
                      <div className="space-y-4">
                        <Input
                          value={currentSection.title}
                          onChange={(e) => {
                            const updatedSections = proposalData.sections.map(section =>
                              section.id === selectedSection
                                ? { ...section, title: e.target.value }
                                : section
                            );
                            setProposalData(prev => ({ ...prev, sections: updatedSections }));
                          }}
                          className="text-2xl font-bold border-none shadow-none"
                        />
                        <div
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning={true}
                          onBlur={(e) => handleSectionEdit(selectedSection, e.currentTarget.textContent || '')}
                          className="min-h-[600px] p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-lg max-w-none"
                          style={{
                            minHeight: '600px',
                            maxHeight: '800px',
                            overflowY: 'auto'
                          }}
                        >
                          {currentSection.content}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Save className="w-4 h-4" />
                          <span>Changes are saved automatically when you click outside the editor</span>
                        </div>
                      </div>
                    ) : (
                      <div
                        ref={editorRef}
                        className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-em:text-gray-600 prose-code:text-gray-800 prose-pre:text-gray-800 prose-blockquote:text-gray-600 prose-li:text-gray-700"
                      >
                        {currentSection.content && currentSection.content.trim() ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              // Enhanced headings with perfect spacing
                              h1: ({ children }) => (
                                <h1 className="text-4xl font-bold text-gray-900 mb-6 mt-8 first:mt-0 border-b-2 border-blue-200 pb-3">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className="text-3xl font-semibold text-gray-800 mb-5 mt-8 border-l-4 border-blue-500 pl-4">
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-6">
                                  {children}
                                </h3>
                              ),
                              h4: ({ children }) => (
                                <h4 className="text-xl font-medium text-gray-800 mb-3 mt-5">
                                  {children}
                                </h4>
                              ),
                              // Enhanced paragraphs
                              p: ({ children }) => (
                                <p className="text-gray-700 mb-4 leading-relaxed">
                                  {children}
                                </p>
                              ),
                              // Enhanced lists
                              ul: ({ children }) => (
                                <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="text-gray-700 leading-relaxed">
                                  {children}
                                </li>
                              ),
                              // Enhanced emphasis
                              strong: ({ children }) => (
                                <strong className="font-semibold text-gray-900">
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em className="italic text-gray-600">
                                  {children}
                                </em>
                              ),
                              // Enhanced blockquotes
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-blue-300 pl-4 py-2 mb-4 bg-blue-50 text-gray-700 italic">
                                  {children}
                                </blockquote>
                              ),
                              // Enhanced code
                              code: ({ children }) => (
                                <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono">
                                  {children}
                                </code>
                              ),
                              pre: ({ children }) => (
                                <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg mb-4 overflow-x-auto">
                                  {children}
                                </pre>
                              ),
                            }}
                          >
                            {currentSection.content}
                          </ReactMarkdown>
                        ) : (
                          <div className="text-gray-500 italic p-8 text-center">
                            <p>No content available for this section.</p>
                            <p className="text-sm mt-2">Section ID: {currentSection.id}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalEditor;
